import moment from "moment-timezone";

type ResolvedSlotRules = {
  businessHours: {
    minHour: number;
    maxHour: number;
  };
  minDaysAhead: number;
  allowWeekends: boolean;
};

export const DATE_FORMATS = {
  fullDateWithDay: "ddd MMMM D, YYYY",
  fullDateWithDayWithoutDay: "MMM D, YYYY",
  fullDateWithTime: "LLL",
  fullDate: "LL",
  hourMin: "hh:mm",
  hourMin24: "HH:MM",
  hourMinWithAMPM: "hh:mm A",
  yyymmdd: "YYYY-MM-DD",
  hourWithAMPM: "h:mm A",
  dateWithoutComma: "DD MMM YYYY",
};

export const DATEPICKER_DATE_FORMATS = {
  Pp: "Pp",
  MMddyyyy: "MM/dd/yyyy",
  MMDDYYYY: "MM/DD/YYYY",
};

export function formatDate(date, format) {
  return moment(date).format(format);
}

export function isCurrentMonth(date) {
  return moment().format("MM/YYYY") === moment(date).format("MM/YYYY");
}

export function getStartAndEndOfMonth({
  date = new Date(),
  config = { format: null, minDate: null, maxDate: null },
}: any) {
  date = new Date(date);
  let startOfMonth = moment(date).startOf("month").format(config.format);
  let endOfMonth = moment(date).endOf("month").format(config.format);

  if (config.minDate && isCurrentMonth(startOfMonth)) {
    startOfMonth = moment(config.minDate).format(config.format);
  }

  return { startOfMonth, endOfMonth };
}

export function getMonthRangeByDate(date?: Date | null) {
  const targetDate = moment(date || undefined);
  return {
    startDate: targetDate.clone().startOf("month").format(DATE_FORMATS.yyymmdd),
    endDate: targetDate.clone().endOf("month").format(DATE_FORMATS.yyymmdd),
  };
}

export function getDefaultMonthRange() {
  return getMonthRangeByDate();
}

export function isSameDate(date, comparedDate) {
  return date.isSame(comparedDate, "d");
}

export function isTodayBeforeCurrentTime(time) {
  return moment(time).isBefore(moment(moment(), "HH:mm"));
}

export function isDateTimeAfter(datetime) {
  return moment(datetime).isAfter();
}

export function isDateTimeBefore(datetime) {
  return moment(datetime).isBefore();
}

export function getFromAndToDateTimeRange(data) {
  let dateFrom = new Date(data.date);
  let dateTo = new Date(data.date);
  let startTime = data.startTime ? new Date(data.startTime) : null;
  let endTime = data.endTime ? new Date(data.endTime) : null;

  let startTimeHours = startTime.getHours();
  let startTimeMinutes = startTime.getMinutes();
  let endTimeHours = endTime.getHours();
  let endTimeMinutes = endTime.getMinutes();

  dateFrom.setHours(startTimeHours);
  dateFrom.setMinutes(startTimeMinutes);

  dateTo.setHours(endTimeHours);
  dateTo.setMinutes(endTimeMinutes);

  return {
    dateFrom,
    dateTo,
  };
}

export function get45MinIntervals(from, to, isTimeRange) {
  const intervals = [];
  let current = new Date(from);
  const endTime = new Date(to);

  if (!isTimeRange) {
    current.setSeconds(0);
    return [current];
  }

  while (current < endTime) {
    const minute = current.getMinutes();
    const remainder = minute % 15;
    if (remainder !== 0) {
      current.setMinutes(minute + 15 - remainder);
    }
    current.setSeconds(0);
    intervals.push(new Date(current));
    current.setMinutes(current.getMinutes() + 45);
  }

  return intervals;
}

export function isTimeBetween(fromTime, toTime) {
  const currentTime = moment();
  const startTime = moment(fromTime);
  const endTime = moment(toTime);

  return currentTime.isBetween(startTime, endTime, null, "[]");
}

export function getLast100Years() {
  const currentYear = new Date().getFullYear();
  const years = [];

  for (let i = 0; i <= 100; i++) {
    years.push(currentYear - i);
  }

  return years;
}

export function concatSelectedFromToTime(data, timeOnly = false) {
  if (timeOnly) {
    return `${formatDate(
      data?.from,
      DATE_FORMATS.hourMin
    )}-${formatDate(data?.to, DATE_FORMATS.hourMinWithAMPM)}`;
  }

  return `${formatDate(
    data?.from,
    DATE_FORMATS.fullDateWithTime
  )} - ${formatDate(data?.to, DATE_FORMATS.hourMinWithAMPM)}`;
}

export function getSlotRulesFromStore(): ResolvedSlotRules {
  const { store } = require("../../store");
  return store.getState().UI.appSettings.data;
}

function formatHourTo12hLabel(hour: number): string {
  const h = hour % 12 === 0 ? 12 : hour % 12;
  const suffix = hour < 12 ? "AM" : "PM";
  return `${h}${suffix}`;
}

export function formatBusinessHoursRangeLabel(
  minHour: number,
  maxHour: number
): string {
  return `${formatHourTo12hLabel(minHour)} to ${formatHourTo12hLabel(maxHour)}`;
}
